import {
  Component,
  OnDestroy,
  ElementRef,
  AfterViewChecked,
  inject,
  ChangeDetectionStrategy,
  viewChild,
  signal,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { coreSelectors } from 'app/core/state';
import { ChatbotService } from 'app/modules/admin/services/chatbot.service';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import ChatContextInput from 'entity/ChatContextInput';
import ChatInput from 'entity/ChatInput';
import User from 'entity/User';
import ChatType from 'enums/ChatType';
import { filter, ReplaySubject, takeUntil } from 'rxjs';

interface ChatMessage {
  sender: 'user' | 'bot';
  text: string;
  animationState?: 'animating' | 'animated';
  time: Date;
  feedback?: boolean;
}

@Component({
  selector: 'app-knowledge-base-chatbot',
  templateUrl: './knowledge-base-chatbot.component.html',
  styleUrl: './knowledge-base-chatbot.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KnowledgeBaseChatbotComponent extends Loggable implements OnDestroy, AfterViewChecked {
  private destroyed$ = new ReplaySubject<boolean>(1);
  protected readonly user = signal<User | undefined>(undefined);

  protected readonly messages = signal<ChatMessage[]>([]);
  protected readonly newMessage = signal('');
  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal<string | undefined>(undefined);
  protected readonly currentChatId = signal<string | undefined>(undefined);
  protected readonly sessionInitialized = signal(false);
  protected readonly errorInitializing = signal(false);
  protected readonly hasStartedChat = signal(false);

  private readonly previousMessageCount = signal(0);

  protected readonly showOpenText = signal(false);

  protected readonly sampleMessages = signal([
    {
      hasStartedChat: false,
      text: 'Finding Attendee',
      prompt: 'What are ways that I can find other attendees in my scoot meeting?',
    },
    { hasStartedChat: false, text: 'Smart Badges', prompt: 'Tell me about smart badges' },
    {
      hasStartedChat: false,
      text: 'Movement',
      prompt: 'How does moving and navigating around the environment work in scoot?',
    },
    { hasStartedChat: false, text: 'Camera & Microphone', prompt: 'Help me set up my camera and microphone' },
    { hasStartedChat: true, text: 'Navigation', prompt: 'How do I navigate in Scoot?' },
    {
      hasStartedChat: true,
      text: 'Find an Attendee',
      prompt: 'What are ways that I can find other attendees in my scoot meeting?',
    },
    { hasStartedChat: true, text: '🙋‍♂️ FAQs', prompt: 'What are the frequently asked questions about Scoot?' },
  ]);

  public readonly messagesContainer = viewChild.required<ElementRef>('messagesContainer');

  private store = inject(Store);
  private chatbotService = inject(ChatbotService);

  public constructor() {
    super();

    this.store
      .select(coreSelectors.getUser)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((user) => {
        this.user.set(user.user);
      });
  }

  public ngAfterViewChecked(): void {
    if (this.messages().length !== this.previousMessageCount()) {
      this.scrollToBottom();
      this.previousMessageCount.set(this.messages().length);
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public sendMessage(messageOverride?: string, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const messageToSend = (messageOverride || this.newMessage()).trim();

    if (!messageToSend || this.isLoading() || !this.user()) {
      return;
    }

    this.newMessage.set('');
    this.isLoading.set(true);
    this.errorMessage.set(undefined);
    this.hasStartedChat.set(true);

    this.messages.update((msgs) => [
      ...msgs,
      {
        sender: 'user',
        text: messageToSend,
        animationState: 'animating',
        time: new Date(),
      },
    ]);
    this.endLastMessageAnimation('user');

    if (this.sessionInitialized()) {
      this.finalizeSend(messageToSend);
      return;
    }

    this.currentChatId.set(`kb-test-${this.user()!.userId}-${Date.now()}`);
    const contextInput: ChatContextInput = {
      chatId: this.currentChatId()!,
      chatType: ChatType.ROOM,
      hostInfo: { hostUserId: this.user()!.userId, hostFullName: this.user()!.name },
    };

    this.chatbotService
      .initializeSession(contextInput)
      .then(() => {
        this.sessionInitialized.set(true);
        this.finalizeSend(messageToSend);
      })
      .catch((error) => {
        this.errorMessage.set('Failed to start chat session.');
        this.error('Init error:', error);
        this.isLoading.set(false);
      });
  }

  private finalizeSend(messageToSend: string): void {
    if (!this.currentChatId()) {
      this.errorMessage.set('Chat session not initialized.');
      this.isLoading.set(false);
      return;
    }

    const chatInput: ChatInput = {
      chatId: this.currentChatId()!,
      message: messageToSend,
      time: new Date().getTime(),
    };

    this.chatbotService
      .sendMessage(chatInput)
      .then((response) => {
        const reply = response?.message ?? 'Sorry, I didn’t catch that. Please try again.';
        this.messages.update((msgs) => [
          ...msgs,
          {
            sender: 'bot',
            text: reply,
            animationState: 'animating',
            time: new Date(),
          },
        ]);
        this.endLastMessageAnimation('bot');
      })
      .catch((error) => {
        this.errorMessage.set('Failed to send message.');
        this.messages.update((msgs) => [
          ...msgs,
          {
            sender: 'bot',
            text: 'Oops, something went wrong. Try again.',
            animationState: 'animating',
            time: new Date(),
          },
        ]);
        this.endLastMessageAnimation('bot');
        this.error('Send error:', error);
      })
      .finally(() => {
        this.isLoading.set(false);
      });
  }

  private endLastMessageAnimation(type: 'bot' | 'user'): void {
    setTimeout(() => {
      const lastIndex = this.messages().length - 1;
      if (lastIndex >= 0 && this.messages()[lastIndex].sender === type) {
        this.messages.update((msgs) => {
          const updatedMsgs = [...msgs];
          updatedMsgs[lastIndex] = { ...updatedMsgs[lastIndex], animationState: 'animated' };
          return updatedMsgs;
        });
      }
    }, 500);
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      this.messagesContainer().nativeElement?.scrollTo({
        top: this.messagesContainer().nativeElement.scrollHeight,
        behavior: 'smooth',
      });
    }, 50);
  }

  public openChatbotInNewTab() {
    window.open('web/admin/knowledge-base', '_blank');
  }

  public onMessageKeyDown(event: KeyboardEvent): void {
    event.stopPropagation();
  }

  public sendFeedback(message: string, feedback: boolean, index: number): void {
    if (!this.currentChatId()) {
      this.errorMessage.set('Chat session not initialized.');
      return;
    }

    this.messages.update((msgs) => {
      const updatedMsgs = [...msgs];
      updatedMsgs[index] = { ...updatedMsgs[index], feedback };
      return updatedMsgs;
    });

    this.info(`Sending feedback for message: "${message}" with feedback: ${feedback}`);
    this.chatbotService
      .sendMessage({
        chatId: this.currentChatId()!,
        message: message,
        feedback: feedback,
        time: this.messages()[index].time.getTime(),
      })
      .then(() => {
        this.info('Feedback sent successfully.');
      })
      .catch((error) => {
        this.error('Failed to send feedback:', error);
        this.errorMessage.set('Failed to send feedback.');
      });
  }
}
